import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, X, MessageCircle, Mail, Smartphone, Bell, ChevronDown } from 'lucide-react';
import {
  outputChannelsApi,
  type OutputChannelConfig,
  type OutputChannelOption,
} from '../../services/output-channels-api';

interface Props {
  value: OutputChannelConfig[];
  onChange: (channels: OutputChannelConfig[]) => void;
}

function channelKey(c: { source: string; id: string }): string {
  return `${c.source}:${c.id}`;
}

function iconForType(type: string): React.ReactNode {
  const t = type.toLowerCase();
  if (t === 'email') return <Mail className="w-3.5 h-3.5" />;
  if (t === 'sms') return <Smartphone className="w-3.5 h-3.5" />;
  if (t === 'push') return <Bell className="w-3.5 h-3.5" />;
  return <MessageCircle className="w-3.5 h-3.5" />;
}

const Chip: React.FC<{
  icon: React.ReactNode;
  label: string;
  detail?: string;
  onRemove?: () => void;
  locked?: boolean;
  lockedTooltip?: string;
}> = ({ icon, label, detail, onRemove, locked, lockedTooltip }) => (
  <div
    className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium border ${
      locked
        ? 'bg-accent border-primary/40 text-primary'
        : 'bg-muted border-border text-foreground'
    }`}
    title={locked ? lockedTooltip : undefined}
  >
    <span className="flex-shrink-0">{icon}</span>
    <span>{label}</span>
    {detail && <span className="text-muted-foreground">— {detail}</span>}
    {locked ? (
      <span className="ml-1 text-[10px] uppercase tracking-wide text-primary">
        Always
      </span>
    ) : (
      onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 p-0.5 rounded-full hover:bg-accent dark:hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={`Remove ${label}`}
        >
          <X className="w-3 h-3" />
        </button>
      )
    )}
  </div>
);

export const OutputChannelsField: React.FC<Props> = ({ value, onChange }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['output-channels'],
    queryFn: () => outputChannelsApi.getOutputChannels(),
  });

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickAway = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [menuOpen]);

  const allChannels = data?.channels ?? [];
  const selectedKeys = useMemo(() => new Set(value.map(channelKey)), [value]);
  const available = allChannels.filter((c) => !selectedKeys.has(channelKey(c)));

  const findOption = (cfg: OutputChannelConfig): OutputChannelOption | undefined =>
    allChannels.find((c) => c.source === cfg.source && c.id === cfg.id);

  const addChannel = (option: OutputChannelOption) => {
    onChange([...value, { source: option.source, id: option.id }]);
    setMenuOpen(false);
  };

  const removeChannel = (cfg: OutputChannelConfig) => {
    onChange(value.filter((c) => !(c.source === cfg.source && c.id === cfg.id)));
  };

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-3">
        A chat thread is always created in the app. Add additional channels to also receive each run there.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Chip
          icon={<MessageCircle className="w-3.5 h-3.5" />}
          label="Chat"
          locked
          lockedTooltip="Always included — every run creates a chat thread you can open in the app."
        />

        {value.map((cfg) => {
          const opt = findOption(cfg);
          return (
            <Chip
              key={channelKey(cfg)}
              icon={iconForType(opt?.type ?? 'unknown')}
              label={opt?.label ?? `${cfg.source}:${cfg.id}`}
              detail={opt?.detail}
              onRemove={() => removeChannel(cfg)}
            />
          );
        })}

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            disabled={isLoading || available.length === 0}
            className="inline-flex items-center gap-1 pl-2 pr-2.5 py-1 rounded-full text-xs font-medium border border-dashed border-input dark:border-white/15 text-foreground hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add destination
            <ChevronDown className="w-3 h-3" />
          </button>

          {menuOpen && available.length > 0 && (
            <div className="absolute left-0 mt-2 w-72 z-10 rounded-xl bg-background border border-border shadow-lg max-h-72 overflow-y-auto">
              <ul className="py-1">
                {available.map((opt) => (
                  <li key={channelKey(opt)}>
                    <button
                      type="button"
                      onClick={() => addChannel(opt)}
                      className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-accent transition-colors"
                    >
                      <span className="mt-0.5 text-muted-foreground">
                        {iconForType(opt.type)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-foreground truncate">
                          {opt.label}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {opt.type.toUpperCase()}
                          {opt.detail ? ` — ${opt.detail}` : ''}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {!isLoading && allChannels.length === 0 && (
        <p className="text-xs text-muted-foreground mt-3">
          No external channels configured yet. Add an email, SMS, or other destination from Settings → Notifications to make them available here.
        </p>
      )}
    </div>
  );
};

export default OutputChannelsField;
